import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, X, ArrowUpRight, Leaf, MapPin, Sun, FilePlus2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* /varieties/directory — searchable, filterable directory of 76 Saudi cultivars (table/card hybrid). */
export default function CultivarDirectoryPage() {
  const { lang, t } = useApp();
  const auth = useAuth();
  const isAdmin = auth?.user?.role === "admin";
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [season, setSeason] = useState("");
  const [texture, setTexture] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => { axios.get(`${API}/cultivars`).then((r) => setData(r.data)).catch(() => setData({ items: [], regions: [], seasons: [], textures: [] })); }, []);

  const items = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.items.filter((c) => (!region || c.regions.includes(region)) && (!season || c.season === season) && (!texture || c.texture === texture)
      && (!needle || `${c.name_ar} ${c.name_en} ${c.locations_ar} ${c.locations_en} ${c.texture_ar} ${c.texture_en}`.toLowerCase().includes(needle)));
  }, [data, q, region, season, texture]);

  const L = (c, k) => c[`${k}_${lang}`] || c[`${k}_${lang === "ar" ? "en" : "ar"}`];
  const reset = () => { setQ(""); setRegion(""); setSeason(""); setTexture(""); };
  const filtered = q || region || season || texture;

  return (
    <main className="content-page collection-page dir-page" data-testid="cultivar-directory-page">
      <Seo title={t("dir.title")} description={t("dir.sub")} />
      <div className="collection-head">
        <Breadcrumbs items={[{ label: t("a7.nav.varieties"), to: "/varieties" }, { label: t("dir.crumb") }]} />
        <span className="section-tag"><Leaf size={12} /> {t("dir.tag")}</span>
        <h1 className="section-title" data-testid="dir-title">{t("dir.title")}</h1>
        <p className="section-sub">{t("dir.sub")}</p>
      </div>
      <div className="dir-body">

        <div className="dir-tools glass-card" data-testid="dir-tools">
          <label className="dir-search"><Search size={16} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("dir.searchPh")} data-testid="dir-search-input" />{q && <button type="button" onClick={() => setQ("")} aria-label="clear" data-testid="dir-search-clear"><X size={14} /></button>}</label>
          <select className="p3-input dir-select" value={region} onChange={(e) => setRegion(e.target.value)} data-testid="dir-filter-region"><option value="">{t("dir.allRegions")}</option>{(data?.regions || []).map((r) => <option key={r} value={r}>{t(`dir.r.${r}`)}</option>)}</select>
          <select className="p3-input dir-select" value={season} onChange={(e) => setSeason(e.target.value)} data-testid="dir-filter-season"><option value="">{t("dir.allSeasons")}</option>{(data?.seasons || []).map((s) => <option key={s} value={s}>{t(`dir.s.${s}`)}</option>)}</select>
          <select className="p3-input dir-select" value={texture} onChange={(e) => setTexture(e.target.value)} data-testid="dir-filter-texture"><option value="">{t("dir.allTextures")}</option>{(data?.textures || []).map((s) => <option key={s} value={s}>{t(`dir.t.${s}`)}</option>)}</select>
          <span className="dir-count" data-testid="dir-count">{items.length} / {data?.total ?? "…"}</span>
          {filtered && <button type="button" className="k-retry" onClick={reset} data-testid="dir-reset">{t("dir.reset")}</button>}
        </div>

        {!data && <div className="dir-loading" data-testid="dir-loading">{[0, 1, 2, 3].map((i) => <div key={i} className="glass-card dir-row skeleton" />)}</div>}
        {data && items.length === 0 && <div className="glass-card k-error soft" data-testid="dir-empty">{t("dir.empty")}</div>}

        <div className="dir-list" role="list" data-testid="dir-list">
          {items.map((c) => {
            const isOpen = open === c.slug;
            return (
              <article key={c.slug} role="listitem" className={`glass-card dir-row ${isOpen ? "open" : ""}`} data-testid={`dir-row-${c.slug}`}>
                <button type="button" className="dir-row-main" onClick={() => setOpen(isOpen ? null : c.slug)} aria-expanded={isOpen} data-testid={`dir-row-toggle-${c.slug}`}>
                  <span className="dir-num">{String(c.order).padStart(2, "0")}</span>
                  <span className="dir-names"><strong>{L(c, "name")}</strong><em dir={lang === "ar" ? "ltr" : "rtl"}>{lang === "ar" ? c.name_en : c.name_ar}</em></span>
                  <span className="dir-chips">
                    <span className={`dir-chip s-${c.season}`}><Sun size={11} /> {t(`dir.s.${c.season}`)}</span>
                    <span className="dir-chip">{t(`dir.t.${c.texture}`)}</span>
                    <span className="dir-chip muted"><MapPin size={11} /> {c.regions.slice(0, 2).map((r) => t(`dir.r.${r}`)).join(" · ")}</span>
                  </span>
                </button>
                {isOpen && (
                  <div className="dir-detail" data-testid={`dir-detail-${c.slug}`}>
                    <dl>
                      <div><dt>{t("dir.origin")}</dt><dd>{L(c, "origin")}</dd></div>
                      <div><dt>{t("dir.seasonWindow")}</dt><dd>{L(c, "season")}</dd></div>
                      <div><dt>{t("dir.texture")}</dt><dd>{L(c, "texture")}</dd></div>
                      <div><dt>{t("dir.locations")}</dt><dd>{L(c, "locations")}</dd></div>
                      <div className="wide"><dt>{t("dir.profile")}</dt><dd>{L(c, "profile")}</dd></div>
                    </dl>
                    {c.featured_slug && <Link to={`/varieties/${c.featured_slug}`} className="btn-lux-ghost dir-featured" data-testid={`dir-featured-${c.slug}`}>{t("dir.readFeatured")} <ArrowUpRight size={14} /></Link>}
                    {!c.featured_slug && isAdmin && (
                      <Link
                        to={`/admin/varieties?create=${c.slug}&ar=${encodeURIComponent(c.name_ar)}&en=${encodeURIComponent(c.name_en)}`}
                        className="btn-lux-gold dir-create"
                        data-testid={`dir-create-page-${c.slug}`}
                      >
                        <FilePlus2 size={14} /> {t("dir.createPage")}
                      </Link>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
        <p className="dir-note" data-testid="dir-note">{t("dir.note")}</p>
      </div>
    </main>
  );
}
